<script setup lang="ts">
import { debounce } from 'lodash-es';
import {
  computed,
  onMounted,
  ref,
} from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';

import type {
  IForgotPasswordTokenSchema,
} from '@modules/forgot-password/types';
import type {
  FormFlattenKey,
  FormFlattenValue,
  FormSchema,
  VFormRef,
} from '@modules/forms/components/VForm/types';
import {
  FORGOT_PASSWORD_ENTER_PASSWORD_ROUTE_NAME,
  TRANSLATION_GROUP_FORGOT_PASSWORD,
} from '@modules/forgot-password/constants';
import { useForgotPasswordConfirmationMutation } from '@modules/forgot-password/mutations';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { FORM_INPUT_DEBOUNCE_DEFAULT_MS } from '@modules/forms/constants';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import {
  assert,
  isOptionalString,
  isString,
} from '@utils/guards';

const { getTranslation } = useI18n();
const route = useRoute();
const router = useRouter();

const innerToken = ref<string>();
const routeToken = computed(() => (isString(route.params.token) ? route.params.token : ''));

const {
  mutateAsync,
  isLoading,
  errors,
  clearError,
} = useForgotPasswordConfirmationMutation();

const isDisabled = computed(() => !innerToken.value || !!Object.keys(errors.value ?? {}).length);

const form = ref<VFormRef>();

const schema = computed<FormSchema<IForgotPasswordTokenSchema>>(() => ({
  reset_password_token: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_FORGOT_PASSWORD, 'CONFIRM_TOKEN').value,
    value: innerToken.value,
    required: true,
  },
}));

function onFormChange(): void {
  form.value?.submit();
}

const debouncedChange = debounce(onFormChange, FORM_INPUT_DEBOUNCE_DEFAULT_MS);

onMounted(() => {
  if (routeToken.value) {
    innerToken.value = routeToken.value;
    onFormChange();
  }
});

async function onSubmit(values: IForgotPasswordTokenSchema): Promise<void> {
  await mutateAsync(values);

  void router.replace({
    name: FORGOT_PASSWORD_ENTER_PASSWORD_ROUTE_NAME,
    params: {
      token: values.reset_password_token,
    },
  });
}

function onInteracted<
  T extends IForgotPasswordTokenSchema,
  K extends FormFlattenKey<T>,
>(
  key: K,
  value: FormFlattenValue<T, K> | undefined,
): void {
  if (key === 'reset_password_token') {
    assert(isOptionalString(value));
    innerToken.value = value;
  }

  clearError(key);

  debouncedChange();
}
</script>

<template>
  <div v-data-qa-auto-id="'ForgotPasswordConfirmPage'" class="input-max-width">
    <div class="text-center text-3xl font-light uppercase">
      {{ $getTranslation(TRANSLATION_GROUP_FORGOT_PASSWORD, 'CONFIRM_HEADER') }}
    </div>
    <div class="my-4 text-center text-muted-color">
      {{ $getTranslation(TRANSLATION_GROUP_FORGOT_PASSWORD, 'CONFIRM_TEXT') }}
    </div>
    <VForm
      :schema="schema"
      :disabled="isDisabled"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_FORGOT_PASSWORD, 'CONFIRM_SUBMIT')"
      is-buttons-centered
      :custom-errors="errors"
      :is-loading="isLoading"
      @interacted="onInteracted"
      @submit="onSubmit"
    />
  </div>
</template>
